import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForceUpdate } from '@mb/lib';
import { Brand } from 'code/MIT/HomeProduct/view/frontend/pages/BrandsPage/redux/sliceCreators/brandsSlice';

import { ResultItem } from 'components/components/SearchAutocompleteResults/SearchAutocompleteResults.types';
import { debounce } from 'lodash';
import {
  getQuerySelector,
  getSuggestionsSelector,
  getIsOpenSelector,
  getHistorySelector,
  getDefaultSuggestionsSelector,
  getSuggestedItemsSelector,
  currentItemIndexSelector,
  currentProductIndexSelector,
} from '../../redux/selectors/autocompleteSelector';
import {
  setCurrentItemIndex,
  setCurrentProductIndex,
  setHoveredItem,
  setShadowQuery,
} from '../../redux/sliceCreators/autocompleteSlice';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { ArrowEvents, RecommendationItem } from '../../types';

const didYouMeanLabel = 'didyoumean';

export function useAutocompleteEventListeners() {
  const query = useAppSelector(getQuerySelector);
  const suggestionsList = useAppSelector(getSuggestionsSelector);
  const suggestedItems = useAppSelector(getSuggestedItemsSelector);
  const searchHistory = useAppSelector(getHistorySelector);
  const isOpen = useAppSelector(getIsOpenSelector);
  const defaultSuggestions = useAppSelector(getDefaultSuggestionsSelector);
  const currentIndex = useAppSelector(currentItemIndexSelector);
  const currentProductIndex = useAppSelector(currentProductIndexSelector);
  const [brands, setBrands] = useState<Brand[]>([]);
  const dispatch = useAppDispatch();
  const forceUpdate = useForceUpdate();

  const shouldShowSuggestions = true;
  const suggestedItemsToShow: RecommendationItem[] = suggestedItems;
  const shouldShowSuggestedItems = false; // suggestedItemsToShow.length > 0 && !isMobile && !isBrands;
  const shouldOpen = isOpen;

  const { suggestions, correctionQuery } = useMemo(() => {
    if (!Array.isArray(suggestionsList?.items)) {
      return {
        suggestions: undefined,
        correctionQuery: undefined,
      };
    }
    const correctionQuery = suggestionsList?.items?.find(
      (item: any) => item.sourceType === didYouMeanLabel,
    );
    const suggestions = {
      ...suggestionsList,
      items: suggestionsList?.items?.filter(
        (item: any) => item.sourceType !== didYouMeanLabel,
      ),
    };
    return {
      correctionQuery,
      suggestions,
    };
  }, [suggestionsList]);

  const defaultResults = useMemo(() => {
    return query.length > 2
      ? suggestions
        ? [suggestions]
        : []
      : defaultSuggestions
      ? [defaultSuggestions].slice(0, 5)
      : [];
  }, [query, suggestions, defaultSuggestions]);

  const resultItems =
    query?.length > 2
      ? defaultResults?.[0]?.items
      : [...searchHistory, ...(defaultResults?.[0]?.items || [])];

  const handleItemHover = (
    value: string,
    index: number = 0,
    item: ResultItem,
  ) => {
    dispatch(setCurrentProductIndex(undefined));
    dispatch(setHoveredItem(item));
    if (index != null) {
      const isTrending = item?.type === 'trending';
      const searchHistoryLength = searchHistory?.length;
      const newIndex =
        query?.length < 3 && isTrending ? searchHistoryLength + index : index;
      dispatch(setCurrentItemIndex(newIndex));
    }
  };

  const handleArrowUp = () => {
    if (currentProductIndex != null) {
      if (currentProductIndex - 3 < 0) {
        dispatch(setCurrentProductIndex(undefined));
        return;
      } else {
        dispatch(setCurrentProductIndex(currentProductIndex - 3));
      }
    } else {
      let newIndex = currentIndex;
      if (currentIndex === undefined || currentIndex === 0) {
        // reset if top end of list
        if (currentIndex === 0) {
          dispatch(setCurrentItemIndex(undefined));
          dispatch(setShadowQuery(undefined));
        }
        return;
      } else {
        newIndex = currentIndex - 1;
      }
      const newValue = resultItems?.[newIndex]?.value;
      if (newValue != null) {
        dispatch(setShadowQuery(newValue));
        handleItemHover(newValue, newIndex, resultItems?.[newIndex]);
      }
      dispatch(setCurrentItemIndex(newIndex));
    }
  };

  const handleArrowDown = () => {
    if (currentProductIndex != null) {
      if (currentProductIndex + 3 > suggestedItems?.length - 1) {
        dispatch(setCurrentProductIndex(undefined));
        return;
      } else {
        dispatch(setCurrentProductIndex(currentProductIndex + 3));
      }
    } else {
      let newIndex = currentIndex;
      if (
        currentIndex === undefined ||
        currentIndex === resultItems?.length - 1
      ) {
        // reset if bottom end of list
        if (currentIndex === resultItems?.length - 1) {
          dispatch(setCurrentItemIndex(undefined));
          dispatch(setShadowQuery(undefined));
          return;
        } else {
          newIndex = 0;
        }
      } else {
        newIndex = currentIndex + 1;
      }
      const newValue = resultItems?.[newIndex]?.value;
      if (newValue != null) {
        dispatch(setShadowQuery(newValue));
        handleItemHover(newValue, newIndex, resultItems?.[newIndex]);
      }
      dispatch(setCurrentItemIndex(newIndex));
    }
  };

  const handleArrowLeft = () => {
    if (currentIndex == null) {
      return;
    }
    let newIndex = currentProductIndex;
    if (currentProductIndex === 0 || currentProductIndex === 3) {
      dispatch(setCurrentProductIndex(undefined));
      return;
    }
    if (currentProductIndex === undefined) {
      newIndex = suggestedItemsToShow?.length - 1;
    } else {
      newIndex = currentProductIndex - 1;
    }
    dispatch(setCurrentProductIndex(newIndex));
  };

  const handleArrowRight = () => {
    if (currentIndex == null) {
      return;
    }

    let newIndex = currentProductIndex;
    if (
      currentProductIndex === 2 ||
      currentProductIndex === suggestedItemsToShow?.length - 1
    ) {
      dispatch(setCurrentProductIndex(undefined));
      return;
    }
    if (currentProductIndex === undefined) {
      newIndex = 0;
    } else {
      newIndex = currentProductIndex + 1;
    }
    dispatch(setCurrentProductIndex(newIndex));
  };

  useEffect(() => {
    if (!shouldShowSuggestedItems) return;

    window.addEventListener(ArrowEvents.Left, handleArrowLeft);
    window.addEventListener(ArrowEvents.Right, handleArrowRight);
    return () => {
      window.removeEventListener(ArrowEvents.Left, handleArrowLeft);
      window.removeEventListener(ArrowEvents.Right, handleArrowRight);
    };
  }, [
    currentIndex,
    suggestedItemsToShow,
    currentProductIndex,
    shouldShowSuggestedItems,
  ]);

  useEffect(() => {
    window.addEventListener(ArrowEvents.Up, handleArrowUp);
    window.addEventListener(ArrowEvents.Down, handleArrowDown);
    return () => {
      window.removeEventListener(ArrowEvents.Up, handleArrowUp);
      window.removeEventListener(ArrowEvents.Down, handleArrowDown);
    };
  }, [currentIndex, defaultResults, searchHistory, query, currentProductIndex]);

  const handleWindowResize = useCallback(
    debounce(() => {
      forceUpdate();
    }, 150),
    [],
  );

  useEffect(() => {
    if (shouldOpen) {
      window.addEventListener('resize', handleWindowResize);
      return () => window.removeEventListener('resize', handleWindowResize);
    }
    return;
  }, [shouldOpen, handleWindowResize]);

  useEffect(() => {
    document.addEventListener('searchItemsChange', (event: CustomEvent) => {
      setBrands(event.detail.brands ?? []);
    });
    return () => {
      document.removeEventListener('searchItemsChange', () => {});
    };
  }, []);

  return {
    brands,
    shouldShowSuggestions,
    shouldShowSuggestedItems,
    correctionQuery,
    results: defaultResults,
    resultItems,
  };
}
